import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Edit as EditIcon } from '@mui/icons-material';
import { isEmpty, orderBy, toString } from 'lodash';
import moment from 'moment';
import queryString from 'query-string';
import { useLocation, useNavigate } from 'react-router-dom';
import { ERROR_403 } from 'common/config/api';
import { notFoundPath } from 'common/config/urls';
import {
  ALLOCATION_VERSION_FILTER,
  ALLOCATION_VERSION_FILTER_TITLE,
  EDIT_MARKET_DATA,
} from 'common/constants/pageFilters';
import { useStore } from 'common/store';
import { LayoutContextValues, SetPageFilters, UseStoreValues } from 'common/types/store';
import { UnsavedChangesValues } from 'common/types/unsavedChanges';
import { useHeaderLiveMetrics } from 'context/HeaderLiveMetricsContext';
import LayoutContext from 'context/LayoutContext';
import UnsavedChangesContext from 'context/UnsavedChangesContext';
import Scalar from 'icons/Scalar';
import { UseAllocationVersionQueryStringParams } from 'pages/ValuationsAllocation/types';
import { useGetAllocationsVersionsByCompanyIdByDateId } from 'services/hooks/company';
import { useAllocationValuesStore } from 'store';
import { formatDateView, getArrayValue, isFalseStrict, MOMENT_DEFAULT_DATE_FORMAT } from 'utilities';
import { AllocationVersionName } from './types';

const useAllocationVersionQueryString = (params: UseAllocationVersionQueryStringParams) => {
  const { selectedMeasurementDate, shouldShowMarketDateOnFilter, shouldShowVersionFilter } = params;

  const [isForbidden, setIsForbidden] = useState(false);
  const [{ fundList, companyInfo }] = useStore() as unknown as UseStoreValues;

  const { showPageForm, togglePageForm, pageFilters, setPageFilters, viewOnlyUser } = useContext(
    LayoutContext
  ) as unknown as LayoutContextValues<AllocationVersionName>;

  const { action: hasChanges, showDialog } = useContext(UnsavedChangesContext) as unknown as UnsavedChangesValues;

  const [selectedVersion, setSelectedVersion] = useState<AllocationVersionName | null>(null);
  const [allocationVersions, setAllocationVersions] = useState<AllocationVersionName[]>();
  const [matchedVersion, setMatchedVersion] = useState<AllocationVersionName | null>(null);
  const [isMarketDialogOpen, setIsMarketDialogOpen] = useState(false);

  const [marketDate, setMarketDate] = useState<string | undefined>();

  const [marketCurrency, setMarketCurrency] = useState<string | undefined>(
    selectedVersion?.market_currency ?? (companyInfo?.financials_currency as string)
  );

  useEffect(() => {
    if (selectedVersion?.market_currency) {
      setMarketCurrency(selectedVersion.market_currency);
    } else {
      setMarketCurrency(companyInfo?.financials_currency as string);
    }
  }, [companyInfo, selectedVersion]);

  const navigate = useNavigate();
  const location = useLocation();

  const getAllocationVersions = useGetAllocationsVersionsByCompanyIdByDateId();

  // Allocation Values Store
  const setCurrentAllocationStatus = useAllocationValuesStore(state => state.setCurrentAllocationStatus);
  const { setLiveEquityValue, setLiveFirmTotal } = useHeaderLiveMetrics();
  const setCurrentAllocationVersion = useAllocationValuesStore(state => state.setCurrentAllocationVersion);
  const setCurrentAllocationId = useAllocationValuesStore(state => state.setCurrentAllocationId);

  const prevMeasurementDateIdRef = useRef<number | undefined>(undefined);
  const prevCompanyIdRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!companyInfo?.id || !selectedMeasurementDate?.id || companyInfo.id !== selectedMeasurementDate.company_id) {
      return;
    }

    const mdId = selectedMeasurementDate.id;
    const companyId = companyInfo.id;

    const measurementDateChanged =
      prevMeasurementDateIdRef.current !== undefined && prevMeasurementDateIdRef.current !== mdId;
    const companyChanged = prevCompanyIdRef.current !== undefined && prevCompanyIdRef.current !== companyId;

    prevMeasurementDateIdRef.current = mdId;
    prevCompanyIdRef.current = companyId;

    if (!measurementDateChanged && !companyChanged) {
      return;
    }

    setSelectedVersion(null);
    setMatchedVersion(null);
    setAllocationVersions(undefined);
    setCurrentAllocationStatus(null);
    setCurrentAllocationId(null);
    setLiveEquityValue(null);
    setLiveFirmTotal(null);
    if (selectedMeasurementDate.date) {
      setMarketDate(moment(selectedMeasurementDate.date).format(MOMENT_DEFAULT_DATE_FORMAT));
    } else {
      setMarketDate(undefined);
    }
  }, [
    companyInfo?.id,
    selectedMeasurementDate?.id,
    selectedMeasurementDate?.company_id,
    selectedMeasurementDate?.date,
    setCurrentAllocationId,
    setCurrentAllocationStatus,
    setLiveEquityValue,
    setLiveFirmTotal,
  ]);

  useEffect(() => {
    setCurrentAllocationVersion?.(selectedVersion?.id ?? null);
  }, [selectedVersion, setCurrentAllocationVersion]);

  const editIcon = useMemo(() => React.createElement(EditIcon, { fontSize: 'small' }), []);

  const handleIconClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      togglePageForm();
    },
    [togglePageForm]
  );

  const defaultVersion = useMemo(() => {
    if (!isEmpty(allocationVersions)) {
      const finalVersion = allocationVersions?.find(v => v.is_final);
      const publishedVersion = allocationVersions?.find(v => v.is_published);
      return finalVersion || publishedVersion || allocationVersions?.[0];
    }
    return null;
  }, [allocationVersions]);

  const versionQueryString = useMemo(() => {
    const { version } = queryString.parse(location.search);
    return version;
  }, [location]);

  useEffect(() => {
    const getAllocationVersionsData = async () => {
      if (selectedMeasurementDate) {
        const versionsResult = await getAllocationVersions(companyInfo?.id, selectedMeasurementDate.id, true);
        if (versionsResult === ERROR_403) {
          setIsForbidden(true);
        } else if (versionsResult && versionsResult.length > 0 && versionsResult !== ERROR_403) {
          setAllocationVersions(versionsResult);
        }
      }
    };
    if (companyInfo?.id === selectedMeasurementDate?.company_id && companyInfo?.id && selectedMeasurementDate?.id) {
      getAllocationVersionsData();
    }
  }, [companyInfo, getAllocationVersions, selectedMeasurementDate]);

  const versionName = useMemo(() => {
    if (!selectedVersion) {
      return '';
    }

    return `Version ${selectedVersion.version} - ${toString(selectedVersion.name)}`;
  }, [selectedVersion]);

  const matchVersion = useCallback(
    version =>
      allocationVersions?.find(item => item.slug === version || item.id?.toString() === version.toString()) || null,
    [allocationVersions]
  );

  useEffect(() => {
    const version = versionQueryString || defaultVersion?.id;
    if (version) {
      const matchedVersionRes = matchVersion(version);
      if (matchedVersionRes?.slug !== versionQueryString || matchedVersion?.slug !== matchedVersionRes?.slug) {
        setMatchedVersion(matchedVersionRes);
      }
    }
  }, [versionQueryString, defaultVersion, matchVersion, matchedVersion]);

  const parsedQuery = useMemo(() => queryString.parse(location.search), [location.search]);

  const setQueryString = useCallback(
    (paramName, newQueryString) => {
      parsedQuery[paramName] = newQueryString;
      navigate({ pathname: location.pathname, search: queryString.stringify(parsedQuery) }, { replace: true });
    },
    [navigate, location, parsedQuery]
  );

  useEffect(() => {
    const versionParam = Array.isArray(versionQueryString) ? versionQueryString[0] : versionQueryString;
    if (!allocationVersions?.length || !defaultVersion?.slug || !versionParam) {
      return;
    }
    if (matchVersion(versionParam)) {
      return;
    }
    setQueryString('version', defaultVersion.slug);
  }, [allocationVersions, defaultVersion, matchVersion, setQueryString, versionQueryString]);

  const pushQueryString = useCallback(
    (paramName, newQueryString) => {
      if (newQueryString && paramName) {
        parsedQuery[paramName] = newQueryString;
        navigate({ pathname: location.pathname, search: queryString.stringify(parsedQuery) });
      }
    },
    [navigate, location.pathname, parsedQuery]
  );

  const setVersionQueryString = useCallback(
    mv => {
      if (
        !selectedVersion ||
        selectedVersion?.id?.toString() !== mv.id.toString() ||
        selectedVersion.slug !== mv.slug
      ) {
        setSelectedVersion(mv);
        if (mv?.market_date) {
          setMarketDate(moment(mv.market_date).format(MOMENT_DEFAULT_DATE_FORMAT));
        } else {
          setMarketDate(moment(selectedMeasurementDate?.date).format(MOMENT_DEFAULT_DATE_FORMAT));
        }
        setQueryString('version', mv.slug);
      }
    },
    [selectedVersion, selectedMeasurementDate, setQueryString]
  );

  const handleAllocationVersionChange = useCallback(
    versionId => {
      const version = allocationVersions?.find(allocationVersion => allocationVersion?.id?.toString() === versionId);
      setVersionQueryString(version);
    },
    [allocationVersions, setVersionQueryString]
  );

  const handleAllocationVersionQueryString = useCallback(() => {
    if (allocationVersions) {
      if (matchedVersion) {
        setVersionQueryString(matchedVersion);
      } else {
        navigate(notFoundPath);
      }
    }
  }, [allocationVersions, matchedVersion, setVersionQueryString, navigate]);

  const onVersionChange = useCallback(
    event => {
      // If there are more thant one Allocation version
      if (getArrayValue(allocationVersions)?.length > 1) {
        // Reset current Allocation Status
        setCurrentAllocationStatus?.(null);
        setLiveEquityValue(null);
        setLiveFirmTotal(null);
      }

      const callback = () => {
        const newVersionId = event.target.value;
        // we check this because if we click 'edit market date' there is not allocation id
        if (newVersionId) {
          handleAllocationVersionChange(newVersionId);
        }
      };

      if (hasChanges) {
        showDialog(callback);
      } else {
        callback();
      }
    },
    [
      allocationVersions,
      handleAllocationVersionChange,
      hasChanges,
      setCurrentAllocationStatus,
      setLiveEquityValue,
      setLiveFirmTotal,
      showDialog,
    ]
  );

  const marketDateDisplay = useMemo(() => (marketDate ? `Market Date: ${marketDate}` : ''), [marketDate]);

  const orderedAllocationsVersions = useMemo(() => {
    if (!allocationVersions?.length) return [];
    return orderBy(allocationVersions, 'version', 'asc').map(version => {
      const versionName = `Version ${version.version} - ${toString(formatDateView(version.name as moment.MomentInput))}`;
      const isSelected = version.id === selectedVersion?.id;
      return {
        ...version,
        label: versionName,
        name: isFalseStrict(version.is_firm_owned) ? (
          <p>
            {versionName} <Scalar className="scalar-version-icon" />
          </p>
        ) : (
          versionName
        ),
        ...(isSelected && {
          icon: editIcon,
          onIconClick: handleIconClick,
        }),
      };
    });
  }, [allocationVersions, selectedVersion, editIcon, handleIconClick]);

  useEffect(() => {
    if (
      !isEmpty(allocationVersions) &&
      !isEmpty(fundList) &&
      marketDate &&
      pageFilters &&
      selectedVersion &&
      shouldShowVersionFilter
    ) {
      const pageFilterMap = pageFilters.reduce<{ [key: string]: SetPageFilters<AllocationVersionName> }>(
        (sofar, pageFilter) => {
          if (pageFilter.id) {
            // eslint-disable-next-line no-param-reassign
            sofar[pageFilter.id] = pageFilter;
          }
          return sofar;
        },
        {}
      );
      // search pageFilterMap[ALLOCATION_VERSION_FILTER].options for the option matching the pageFilterMap[ALLOCATION_VERSION_FILTER].selectedValue;
      const allocationFilter = pageFilterMap[ALLOCATION_VERSION_FILTER];
      const filterSubtitle = allocationFilter?.subtitle;
      const selectedValue = allocationFilter?.selectedValue;
      const selectedPageFilter = allocationFilter?.options?.find(version => selectedValue === version.id);
      if (
        selectedValue !== selectedVersion?.id ||
        selectedPageFilter?.slug !== selectedVersion?.slug ||
        (shouldShowMarketDateOnFilter && marketDateDisplay !== filterSubtitle) ||
        (!shouldShowMarketDateOnFilter && !isEmpty(filterSubtitle))
      ) {
        pageFilterMap[ALLOCATION_VERSION_FILTER] = {
          id: ALLOCATION_VERSION_FILTER,
          options: orderedAllocationsVersions,
          title: ALLOCATION_VERSION_FILTER_TITLE,
          selectedValue: selectedVersion?.id,
          handler: onVersionChange,
          extraOptions:
            viewOnlyUser || !shouldShowMarketDateOnFilter
              ? []
              : [
                {
                  label: EDIT_MARKET_DATA,
                  callback: () => setIsMarketDialogOpen(true),
                },
              ],
          subtitle: shouldShowMarketDateOnFilter ? marketDateDisplay : '',
        };
        if (setPageFilters) {
          setPageFilters(Object.values(pageFilterMap));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allocationVersions,
    fundList,
    marketDate,
    pageFilters,
    selectedVersion,
    shouldShowMarketDateOnFilter,
    shouldShowVersionFilter,
    marketDateDisplay,
    orderedAllocationsVersions,
  ]);

  const getVersions = useCallback(
    version => {
      const foundVersion = allocationVersions?.find(v => v.id === version.id);

      if (!foundVersion) {
        const versions = allocationVersions || [];
        const updatedAllocationVersions = [...versions, version];
        setAllocationVersions(updatedAllocationVersions);
        setSelectedVersion(version);
        if (version?.market_date) {
          setMarketDate(moment(version.market_date).format(MOMENT_DEFAULT_DATE_FORMAT));
        } else {
          setMarketDate(moment(selectedMeasurementDate?.date).format(MOMENT_DEFAULT_DATE_FORMAT));
        }
        pushQueryString('version', version.slug);
      } else if (foundVersion) {
        // Check if the version has changed (slug, is_final, or is_published)
        const hasChanged =
          foundVersion.slug !== version.slug ||
          foundVersion.is_final !== version.is_final ||
          foundVersion.is_published !== version.is_published;

        if (hasChanged) {
          const versions = allocationVersions || [];
          const updatedAllocationVersions = versions.map(v => {
            if (v.id === version.id) {
              return version;
            }
            return v;
          });
          setAllocationVersions(updatedAllocationVersions);

          // Update selectedVersion if it matches the updated version
          if (selectedVersion?.id === version.id) {
            setSelectedVersion(version);
          }

          if (foundVersion.slug !== version.slug) {
            pushQueryString('version', version.slug);
          }
        }
      }
    },
    [allocationVersions, selectedMeasurementDate, selectedVersion, pushQueryString]
  );

  useEffect(() => {
    if (selectedMeasurementDate && matchedVersion) {
      handleAllocationVersionQueryString();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMeasurementDate, matchedVersion]);

  const dialogProps = useMemo(() => {
    if (allocationVersions) {
      if (matchedVersion) {
        return {
          open: showPageForm,
          onClose: togglePageForm,
          versions: allocationVersions,
          getVersions,
          matchedVersion,
        };
      }

      return {
        versions: allocationVersions,
      };
    }

    return null;
  }, [showPageForm, togglePageForm, allocationVersions, getVersions, matchedVersion]);

  const marketDialogProps = useMemo(
    () => ({
      isMarketDialogOpen,
      setIsMarketDialogOpen,
      marketDate,
      setMarketDate,
      marketCurrency,
      setMarketCurrency,
    }),
    [isMarketDialogOpen, setIsMarketDialogOpen, marketDate, setMarketDate, marketCurrency, setMarketCurrency]
  );

  const finalAllocationInSameMeasurementDate = useMemo(
    () => allocationVersions?.some(version => version.is_final),
    [allocationVersions]
  );
  return {
    allocationVersions,
    dialogProps,
    finalAllocationInSameMeasurementDate,
    getVersions,
    isForbidden,
    marketDialogProps,
    selectedVersion,
    versionName,
  };
};

export default useAllocationVersionQueryString;
